import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faInfoCircle,
  faDownload,
  faExpand,
  faCalendarDay,
} from "@fortawesome/free-solid-svg-icons";

const ShipmentsByWeekdayChart = ({ data, onChartClick }) => {
  const [activeBar, setActiveBar] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Color settings for bars
  const baseColor = "#3b82f6"; // blue-500
  const highlightColor = "#2563eb"; // blue-600

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-md border border-gray-200 rounded-md">
          <div className="font-medium mb-1">{label}</div>
          <div className="text-gray-700 text-sm">
            Shipments: <span className="font-medium">{payload[0].value}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  // Format weekday labels to 3 letters
  const formatWeekday = (value) => {
    return value.substring(0, 3);
  };

  // No data display
  if (!data || data.length === 0 || data.every((item) => item.count === 0)) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-500">
        <FontAwesomeIcon
          icon={faCalendarDay}
          className="text-3xl mb-2 opacity-50"
        />
        <div>No weekday data available</div>
      </div>
    );
  }

  // Handle bar click
  const handleBarClick = (data, index) => {
    if (typeof onChartClick === "function") {
      onChartClick(data);
    }
  };

  // Find max value for Y-axis configuration
  const maxCount = Math.max(...data.map((item) => item.count));
  const yAxisMax = Math.ceil(maxCount * 1.2); // Add 20% padding

  return (
    <div className="h-full flex flex-col">
      <ResponsiveContainer width="100%" height="85%">
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 10,
            left: 10,
            bottom: 5,
          }}
          onMouseMove={(e) => {
            if (e && e.activeTooltipIndex !== undefined) {
              setActiveBar(e.activeTooltipIndex);
            } else {
              setActiveBar(null);
            }
          }}
          onMouseLeave={() => setActiveBar(null)}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
          <XAxis
            dataKey="name"
            tickFormatter={formatWeekday}
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
          />
          <YAxis
            domain={[0, yAxisMax]}
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
            tickCount={6}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: "rgba(224, 231, 255, 0.2)" }}
          />
          <Bar
            dataKey="count"
            name="Shipments"
            radius={[4, 4, 0, 0]}
            onClick={handleBarClick}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={index === activeBar ? highlightColor : baseColor}
                opacity={
                  index === activeBar ? 1 : activeBar === null ? 0.8 : 0.4
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex justify-center items-center my-2">
        <div className="flex items-center text-sm bg-gray-50 px-3 py-1 rounded-full">
          <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
          <span>Shipments per weekday</span>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-2 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          <span>Click on bars for details</span>
        </div>
        <div className="flex space-x-1">
          <button
            title="Download chart"
            className="p-1 hover:text-blue-600 transition-colors"
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            title="Expand chart"
            className="p-1 hover:text-blue-600 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
        </div>
      </div>

      {/* Expanded View Modal */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsExpanded(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-4/5 h-4/5 max-w-6xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <FontAwesomeIcon
                  icon={faChartBar}
                  className="text-blue-600 mr-2"
                />
                Shipments by Weekday
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsExpanded(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="h-5/6">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 30,
                    bottom: 20,
                  }}
                  onMouseMove={(e) => {
                    if (e && e.activeTooltipIndex !== undefined) {
                      setActiveBar(e.activeTooltipIndex);
                    } else {
                      setActiveBar(null);
                    }
                  }}
                  onMouseLeave={() => setActiveBar(null)}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    opacity={0.3}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 14 }}
                    axisLine={{ stroke: "#e5e7eb" }}
                    tickLine={{ stroke: "#e5e7eb" }}
                  />
                  <YAxis
                    domain={[0, yAxisMax]}
                    tick={{ fontSize: 14 }}
                    axisLine={{ stroke: "#e5e7eb" }}
                    tickLine={{ stroke: "#e5e7eb" }}
                    tickCount={6}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(224, 231, 255, 0.2)" }}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <Bar
                    dataKey="count"
                    name="Shipments"
                    radius={[6, 6, 0, 0]}
                    onClick={handleBarClick}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index === activeBar ? highlightColor : baseColor}
                        opacity={
                          index === activeBar
                            ? 1
                            : activeBar === null
                            ? 0.8
                            : 0.4
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentsByWeekdayChart;
